import { CalendarDates } from "@/custom-types";
import { AudienceCampaignOption, AudinceCampaignOptionResponse, ResourceOptions } from "@/http/api-types";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export const SPLIT_BY_CAMPAIGN = "campaign";
export const SPLIT_BY_CAMPAIGN_DAY = "campaign-day";
export const SPLIT_BY_DAY = "day";

export const ALL_DAYS = "all-days";
export const ALL_CAMPAIGNS = "all-campaigns";
export const TOTAL = "total";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const numberFormat = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
  }).format(value);

export const largeNumberFormat = (value: number) => {
  if (value >= 1000000) return `€${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `€${(value / 1000).toFixed(1)}K`;
  return `€${value.toFixed(0)}`;
};

export const queryDateFormat = (date: Date) =>
  new Intl.DateTimeFormat("fr-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

export const endOfPreviousMonth = () => {
  const date = new Date();

  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
};

export const twoYearsBefore = (date: Date) => {
  return new Date(date.getFullYear() - 2, date.getMonth(), date.getDate(), 0, 0, 0, 0);
};

export const oneYearBefore = (date: Date) => {
  return new Date(date.getFullYear() - 1, date.getMonth(), date.getDate(), 0, 0, 0, 0);
};

export function formatDateToYYYYMMDD(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatDateToMYYYY(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
}

export function getAudienceSelectOptions(
  audienceCampaignData: AudinceCampaignOptionResponse | undefined,
): Record<string, AudienceCampaignOption> {
  const audienceOptions = {} as Record<string, AudienceCampaignOption>;
  audienceCampaignData?.results?.forEach(option => {
    if (!audienceOptions[option.audience_id]) {
      audienceOptions[option.audience_id] = {
        audience_name: option.audience_name,
        organization_id: option.organization_id,
        campaigns: [],
      };
    }
    audienceOptions[option.audience_id].campaigns.push({ id: option.campaign_id, name: option.campaign_name });
  });

  return audienceOptions;
}

export function getCampaignSelectOptions(audienceOptions: Record<string, AudienceCampaignOption>, audience: string) {
  if (audience !== "" && audienceOptions[audience]) {
    return audienceOptions[audience].campaigns;
  }

  return Object.entries(audienceOptions).reduce((acc, current) => {
    return acc.concat(current[1].campaigns);
  }, [] as ResourceOptions[]);
}

export function getCurrencySymbol(currency: string, locale: string) {
  return (
    new Intl.NumberFormat(locale, { style: "currency", currency }).formatToParts(1).find(x => x.type === "currency")
      ?.value ?? ""
  );
}

export const dailyLabelFormatter = (value: string) => {
  const date = new Date(value);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

export function formatUTCDateTime(value?: string): string {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return `${new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
    // 24-hour everywhere: this is a UTC-only ops dashboard, and h23 keeps
    // midnight as 00:00 rather than the 24:00 that hour12:false can produce.
    hourCycle: "h23",
  }).format(date)} UTC`;
}

// Date-only counterpart of formatUTCDateTime, for fields that store an instant
// but mean a calendar day. Unlike formatUTCDateTime it does not append "UTC",
// since a bare day reads better without the suffix.
export function formatUTCDate(value?: string): string {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(date);
}

export const chartDescription = (audience: string, date?: CalendarDates, campaign?: string) => {
  const isSingleDay = date?.from && date.to && date.from.toString() === date.to.toString();

  const dateRange =
    (date?.from &&
      date.to &&
      (isSingleDay
        ? `${dailyLabelFormatter(date.from.toString())}`
        : `${dailyLabelFormatter(date.from.toString())} - ${dailyLabelFormatter(date.to.toString())}`)) ||
    "";

  return `${dateRange} ${campaign ? `for Campaign ${campaign}` : `for Audience ${audience}`}`;
};

export const DEFAULT_TIMEZONE = "Etc/UTC";

export function organizationFilter(organizations: { id: string | number; name: string }[]) {
  return {
    type: "string",
    ref: "organization",
    action: "in",
    values: organizations.map(org => `[${org.id}] ${org.name}`),
  };
}

export function campaignFilter(campaigns: { id: string | number; name: string }[]) {
  return {
    type: "string",
    ref: "campaign",
    action: "in",
    values: campaigns.map(c => `[${c.id}] ${c.name}`),
  };
}

export function labelFilter(labels: { id: string | number; name: string }[]) {
  return {
    type: "string",
    ref: "label",
    action: "in",
    values: labels.map(l => `[${l.id}] ${l.name}`),
  };
}

export function predictionFlightFilter(predictionFlights: { id: string | number; name: string }[]) {
  return {
    type: "string",
    ref: "prediction_flight_ids",
    action: "in",
    values: predictionFlights.map(pf => `[${pf.id}] ${pf.name}`),
  };
}

export function budgetPoolFilter(budgetPools: { id: string | number; name: string }[]) {
  return {
    type: "string",
    ref: "budget_pool",
    action: "in",
    values: budgetPools.map(bp => `[${bp.id}] ${bp.name}`),
  };
}

export function bidDateFilter(bidDate: string) {
  return {
    type: "string",
    ref: "bid_ts",
    action: "contains",
    values: [bidDate],
  };
}

export function daysSinceReengagementFilter(targetCohortDays: number) {
  return {
    type: "number",
    ref: "days_since_reengagement",
    ranges: [
      {
        end: targetCohortDays,
        bounds: "[)",
      },
    ],
  };
}

export function absoluteInterval(params: { startDate: string; endDate: string; includeEndDate?: boolean }) {
  const { startDate, endDate, includeEndDate } = params;

  let end = endDate;
  if (includeEndDate) {
    const d = new Date(endDate);
    d.setDate(d.getDate() + 1);
    end = d.toISOString().slice(0, 10);
  }
  return {
    type: "time",
    ref: "__time",
    timeRanges: [
      {
        start: startDate,
        end: end,
      },
    ],
  };
}

export function relativeInterval(params: { duration: string; step?: number }) {
  const { duration, step = -1 } = params;
  return {
    type: "time",
    ref: "__time",
    timePeriods: [
      {
        duration,
        step,
        type: "latest",
      },
    ],
  };
}

export function splitByTime(granularity: string, sort: "ascending" | "descending" = "ascending") {
  const result: any = {
    type: "time",
    dimension: "__time",
    granularity,
  };
  if (sort) {
    result.sort = {
      ref: "__time",
      direction: sort,
    };
  }
  return result;
}

export function splitByBudgetPool(sortBy: string = "bids") {
  return {
    type: "string",
    dimension: "budget_pool",
    sort: { ref: sortBy, direction: "descending" },
    limit: 50,
  };
}

export function splitByCampaign(sortBy: string = "bids") {
  return {
    type: "string",
    dimension: "campaign",
    sort: { ref: sortBy, direction: "descending" },
    limit: 50,
  };
}

export function splitByPredictionFlight(sortBy: string = "bids") {
  return {
    type: "string",
    dimension: "prediction_flight_ids",
    sort: { ref: sortBy, direction: "descending" },
    limit: 50,
  };
}

export function splitByOrganization(sortBy: string = "bids") {
  return {
    type: "string",
    dimension: "organization",
    sort: { ref: sortBy, direction: "descending" },
    limit: 50,
  };
}

export function splitByLabel(sortBy: string = "bids") {
  return {
    type: "string",
    dimension: "label",
    sort: { ref: sortBy, direction: "descending" },
    limit: 50,
  };
}

export interface PivotPayloadOptions {
  source: string;
  selectedMeasures: string[];
  interval: object;
  visualization?: string;
  filter?: object | object[] | null;
  splits?: object[] | null;
}

export function buildPivotPayload({
  source,
  selectedMeasures,
  interval,
  visualization = "line-chart",
  filter = null,
  splits = null,
}: PivotPayloadOptions) {
  return {
    dataCubeName: source,
    viewDefinitionVersion: "3",
    viewDefinition: {
      visualization,
      timezone: DEFAULT_TIMEZONE,
      filters: [interval].concat(filter ? (Array.isArray(filter) ? filter : [filter]) : []).filter(Boolean),
      splits: splits || [splitByTime("PT1H")],
      measures: selectedMeasures,
    },
  };
}

// Unwraps the API's error shapes (axios response body first, then a plain Error) so a
// failed mutation can show what actually went wrong instead of a generic toast.
export function getErrorMessage(error: unknown): string {
  if (error && typeof error === "object") {
    // Axios error with response
    if ("response" in error && error.response && typeof error.response === "object") {
      const response = error.response as { data?: { error?: string; errors?: { message: string }[] } };
      if (response.data?.error) {
        return response.data.error;
      }
      if (response.data?.errors?.[0]?.message) {
        return response.data.errors[0].message;
      }
    }
    // Error with message
    if ("message" in error && typeof error.message === "string") {
      return error.message;
    }
  }
  return "An unexpected error occurred";
}
